import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRegistrarTelaTestadora } from "@/hooks/useRegistrarTelaTestadora";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Cache em memória do user_type por user.id para evitar refetch a cada navegação
const userTypeCache = new Map<string, string | null>();

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  useRegistrarTelaTestadora();
  const hasShownToast = useRef(false);
  const [userType, setUserType] = useState<string | null | undefined>(
    user ? userTypeCache.get(user.id) : undefined
  );
  const [checkingType, setCheckingType] = useState(false);

  useEffect(() => {
    if (!loading && !user && !hasShownToast.current) {
      const hadSession = typeof window !== 'undefined' && sessionStorage.getItem('had_session') === '1';
      if (hadSession) {
        hasShownToast.current = true;
        sessionStorage.removeItem('had_session');
        toast({
          title: "Sessão Expirada",
          description: "Sua sessão expirou por inatividade. Por favor, faça login novamente.",
          variant: "destructive",
        });
      }
    }
  }, [loading, user, toast]);

  useEffect(() => {
    if (!user) {
      setUserType(undefined);
      return;
    }

    // Usar cache se disponível
    if (userTypeCache.has(user.id)) {
      setUserType(userTypeCache.get(user.id) ?? null);
      return;
    }

    let cancelled = false;
    setCheckingType(true);
    supabase
      .from('profiles')
      .select('user_type')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const type = data?.user_type ?? null;
        userTypeCache.set(user.id, type);
        setUserType(type);
      })
      .then(undefined, () => {
        if (cancelled) return;
        userTypeCache.set(user.id, null);
        setUserType(null);
      })
      .then(() => {
        if (!cancelled) setCheckingType(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading || (user && (userType === undefined || checkingType))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Clientes não podem navegar no app da empresa
  if (userType === 'cliente') {
    return <Navigate to="/client-portal" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
